import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import httpClient from "./component/utils/httpClients";

const PlayerContext = createContext();

export function usePlayer() {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error("usePlayer must be used within a PlayerProvider");
  }
  return context;
}

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);

  const [tracks, setTracks] = useState([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [seek, setSeek] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [isShuffle, setIsShuffle] = useState(false);
  const [isRepeat, setIsRepeat] = useState(false);
  const [queueUpdateTrigger, setQueueUpdateTrigger] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const isFetchingQueueRef = useRef(false);
  const seekThrottleRef = useRef(null);
  const mountedRef = useRef(true);
  const abortControllerRef = useRef(null);
  const playCountTimerRef = useRef(null);
  const playCountTimerContentIdRef = useRef(null);
  const playCountTrackedRef = useRef(new Set());
  const lastTrackIdRef = useRef(null);
  const volumeRef = useRef(volume);
  const isPlayingRef = useRef(isPlaying);
  const userInteractedRef = useRef(userInteracted);
  const pendingAutoPlayRef = useRef(false);

  const normalizeQueue = useCallback((queue) => {
    if (!queue || !Array.isArray(queue.tracks)) return [];
    return queue.tracks.map((t, index) => {
      const track = t.trackId || t || {};
      const isAudio =
        track.fileType === "audio" ||
        (typeof track.mimeType === "string" &&
          track.mimeType.includes("audio")) ||
        (typeof track.fileUrl === "string" &&
          (track.fileUrl.includes(".mp3") || track.fileUrl.includes(".m4a")));

      return {
        _id: track._id || t._id,
        title: track.title || "Unknown",
        artist: track.artist || "Unknown Artist",
        fileUrl: track.fileUrl || track.audioUrl || "",
        coverImageUrl: track.coverImageUrl || track.cover || "",
        duration: track.duration || 0,
        fileType: "audio",
        mimeType: track.mimeType,
        mediaType: "audio",
        originalIndex: index,
        playCount: track.playCount,
      };
    });
  }, []);

  // Initialize
  useEffect(() => {
    mountedRef.current = true;
    const saved = localStorage.getItem("playerVolume");
    if (saved != null) {
      const v = parseFloat(saved);
      if (!isNaN(v)) setVolume(Math.max(0, Math.min(1, v)));
    }
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Volume persistence and sync
  useEffect(() => {
    localStorage.setItem("playerVolume", String(volume));
    volumeRef.current = volume;
    const audio = audioRef.current;
    if (audio) {
      audio.volume = volume;
    }
  }, [volume]);

  const playTrackObject = useCallback(async (track) => {
    if (!track || !track.fileUrl) return false;

    try {
      const audio = audioRef.current;
      if (!audio) return false;

      // Only set new src if different
      if (audio.src !== track.fileUrl) {
        audio.src = track.fileUrl;
      }

      audio.volume = volumeRef.current ?? 0.8;

      // Let the play event handle isPlaying state
      await audio.play();
      return true;
    } catch (err) {
      console.warn("playTrackObject: autoplay prevented or error:", err);
      return false;
    }
  }, []);

  const playMediaByIndex = useCallback(
    async (index) => {
      if (index == null || index < 0 || index >= tracks.length) return;
      const track = tracks[index];
      if (!track) return;

      setUserInteracted(true);
      setCurrentTrackIndex(index);
      await playTrackObject(track);
    },
    [tracks, playTrackObject],
  );

  const fetchCurrentQueue = useCallback(async () => {
    if (isFetchingQueueRef.current) return;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Snapshot local playback state before refresh; used to prevent auto-play on queue updates
    const wasPlayingBeforeRefresh = isPlayingRef.current === true;
    const shouldForceAutoPlay = pendingAutoPlayRef.current === true;

    isFetchingQueueRef.current = true;
    setIsLoading(true);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const res = await httpClient.get(
        "user/queue/current",
        // {},
        {
          signal: controller.signal,
          skipLoader: true,
          skipToast: true,
          skipErrorToast: true,
        },
        // {},
      );

      if (res?.data?.data?.queue) {
        const queue = res.data.data?.queue;
        const currentIdx = queue?.currentIndex ?? 0;
        const normalized = normalizeQueue(queue);

        setTracks(normalized);
        const safeIndex = Math.min(
          Math.max(currentIdx, 0),
          Math.max(normalized.length - 1, 0),
        );
        setCurrentTrackIndex(safeIndex);

        const backendSaysPlaying = queue?.isPlaying === true;
        const trackObj = normalized[safeIndex];

        // Auto-play if:
        //  - user just explicitly requested a track to play (shouldForceAutoPlay), or
        //  - we were already playing and backend says playing.
        if (
          trackObj &&
          (shouldForceAutoPlay ||
            (backendSaysPlaying && wasPlayingBeforeRefresh))
        ) {
          pendingAutoPlayRef.current = false;
          await playTrackObject(trackObj);
        } else if (!wasPlayingBeforeRefresh) {
          const audio = audioRef.current;
          if (audio && !audio.paused) {
            audio.pause();
          }
          if (isPlayingRef.current) setIsPlaying(false);
        }

        setError(null);
      } else {
        console.warn("Queue fetch unsuccessful:", res?.data);
        setTracks([]);
        setError("Failed to fetch queue");
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Failed to fetch queue:", err);
        setTracks([]);
        setError("Network error while fetching queue");
      }
    } finally {
      setIsLoading(false);
      isFetchingQueueRef.current = false;
      abortControllerRef.current = null;
      setIsInitialized(true);
    }
  }, [normalizeQueue, playTrackObject]);

  const triggerQueueUpdate = useCallback(
    () => setQueueUpdateTrigger((prev) => prev + 1),
    [],
  );

  const stopPlayback = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch (err) {
        console.warn("stopPlayback error:", err);
      }
    }
    if (playCountTimerRef.current) {
      clearTimeout(playCountTimerRef.current);
      playCountTimerRef.current = null;
      playCountTimerContentIdRef.current = null;
    }
    setIsPlaying(false);
    isPlayingRef.current = false;
    setSeek(0);
  }, []);

  const playTrackById = useCallback(
    async (trackId, mediaType = "audio") => {
      try {
        stopPlayback();
        pendingAutoPlayRef.current = true;
        setUserInteracted(true);
        setIsLoading(true);
        const payload = {
          action: Array.isArray(trackId) ? "addAllToQueue" : "addAndPlay",
          trackId,
          mediaType,
        };
        const res = await httpClient.post(
          "user/queue/queue-controller",
          payload,
          { skipLoader: true, skipErrorToast: true },
        );
        if (res.data?.success) {
          setError(null);
          triggerQueueUpdate();
          return true;
        } else {
          pendingAutoPlayRef.current = false;
          setError("Failed to play track");
          return false;
        }
      } catch (err) {
        pendingAutoPlayRef.current = false;
        console.error("Error playing track:", err);
        setError("Error playing track");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [stopPlayback, triggerQueueUpdate],
  );

  const handleNextTrack = useCallback(async () => {
    setUserInteracted(true);
    setSeek(0);

    if (!tracks.length) return;
    const currentTrack = tracks[currentTrackIndex];
    if (!currentTrack) return;

    // Set duration from track data immediately
    if (currentTrack.duration && currentTrack.duration > 0) {
      setDuration(currentTrack.duration);
    } else {
      setDuration(0);
    }

    // SHUFFLE ON → Frontend Random Only
    if (isShuffle) {
      let nextIndex;
      do {
        nextIndex = Math.floor(Math.random() * tracks.length);
      } while (nextIndex === currentTrackIndex && tracks.length > 1);

      setCurrentTrackIndex(nextIndex);
      await playMediaByIndex(nextIndex);
      return;
    }

    // REPEAT ON → Loop to First Track
    if (isRepeat) {
      const nextIndex = 0;
      setCurrentTrackIndex(nextIndex);
      await playMediaByIndex(nextIndex);
      return;
    }

    // NORMAL (NO SHUFFLE / NO REPEAT)
    if (currentTrackIndex >= tracks.length - 1) {
      // Stop if last track
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
      }
      return;
    }

    const nextIndex = currentTrackIndex + 1;

    try {
      const res = await httpClient.post(
        "user/queue/queue-controller",
        {
          action: "next",
          trackId: currentTrack._id,
          mediaType: "audio",
        },
        { skipLoader: true, skipErrorToast: true },
      );

      if (res.data?.success) {
        triggerQueueUpdate();
      } else {
        // fallback local next
        setCurrentTrackIndex(nextIndex);
        await playMediaByIndex(nextIndex);
      }
    } catch (err) {
      console.error("Next API failed:", err);
      // fallback local next
      setCurrentTrackIndex(nextIndex);
      await playMediaByIndex(nextIndex);
    }
  }, [
    tracks,
    currentTrackIndex,
    isShuffle,
    isRepeat,
    playMediaByIndex,
    triggerQueueUpdate,
  ]);

  const handlePrevTrack = useCallback(async () => {
    setUserInteracted(true);
    setSeek(0);

    if (!tracks.length) return;
    const currentTrack = tracks[currentTrackIndex];
    if (!currentTrack) return;

    // SHUFFLE ON → Frontend Random Only
    if (isShuffle) {
      let prevIndex;
      do {
        prevIndex = Math.floor(Math.random() * tracks.length);
      } while (prevIndex === currentTrackIndex && tracks.length > 1);

      setCurrentTrackIndex(prevIndex);
      await playMediaByIndex(prevIndex);
      return;
    }

    // REPEAT ON + AT FIRST SONG → Go to Last
    if (isRepeat && currentTrackIndex === 0) {
      const prevIndex = tracks.length - 1;
      setCurrentTrackIndex(prevIndex);
      await playMediaByIndex(prevIndex);
      return;
    }

    // NORMAL (NO SHUFFLE / NO REPEAT)
    if (currentTrackIndex <= 0) {
      // Stop if first track
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
      }
      return;
    }

    const prevIndex = currentTrackIndex - 1;

    try {
      const res = await httpClient.post(
        "user/queue/queue-controller",
        {
          action: "prev",
          trackId: currentTrack._id,
          mediaType: "audio",
        },
        { skipLoader: true, skipErrorToast: true },
      );

      if (res.data?.success) {
        triggerQueueUpdate();
      } else {
        setCurrentTrackIndex(prevIndex);
        await playMediaByIndex(prevIndex);
      }
    } catch (err) {
      console.error("Prev API failed:", err);
      setCurrentTrackIndex(prevIndex);
      await playMediaByIndex(prevIndex);
    }
  }, [
    tracks,
    currentTrackIndex,
    isShuffle,
    isRepeat,
    playMediaByIndex,
    triggerQueueUpdate,
  ]);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      const currentTrack = tracks[currentTrackIndex];
      // Prefer audio.duration if available and valid, otherwise use track duration
      if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      } else if (currentTrack?.duration && currentTrack.duration > 0) {
        setDuration(currentTrack.duration);
      }
    };

    const handleTimeUpdate = () => {
      if (seekThrottleRef.current) return;
      seekThrottleRef.current = requestAnimationFrame(() => {
        if (audio) {
          setSeek(audio.currentTime);
        }
        seekThrottleRef.current = null;
      });
    };

    const handleEnded = () => {
      if (isRepeat) {
        if (audio) {
          try {
            audio.currentTime = 0;
            const playPromise = audio.play();
            if (playPromise && playPromise.catch) {
              playPromise.catch((err) => {
                console.warn("Repeat play blocked:", err);
              });
            }
          } catch (err) {
            console.warn("Repeat play error:", err);
          }
        }
      } else {
        handleNextTrack();
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
      isPlayingRef.current = true;
    };

    const handlePause = () => {
      setIsPlaying(false);
      isPlayingRef.current = false;
    };

    const handleError = (e) => {
      const media = e.target;
      const src = media?.currentSrc || media?.src;
      const error = media?.error;
      const code = error?.code;

      const errorMessage =
        {
          1: "MEDIA_ERR_ABORTED - fetching aborted by user",
          2: "MEDIA_ERR_NETWORK - network issue or 404 file",
          3: "MEDIA_ERR_DECODE - cannot decode audio format",
          4: "MEDIA_ERR_SRC_NOT_SUPPORTED - invalid format or CORS",
        }[code] || "Unknown media error";

      console.error("Audio Playback Error:", {
        file: src,
        code: code,
        message: errorMessage,
      });

      setError(`Playback failed: ${errorMessage}`);
      setIsPlaying(false);
    };

    // Add event listeners
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("error", handleError);

    return () => {
      if (seekThrottleRef.current) {
        cancelAnimationFrame(seekThrottleRef.current);
        seekThrottleRef.current = null;
      }

      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("error", handleError);
    };
  }, [tracks, currentTrackIndex, isRepeat, handleNextTrack]);

  // Handle automatic setup when tracks/currentTrackIndex changes
  useEffect(() => {
    if (!isInitialized || tracks.length === 0) return;
    const currentTrack = tracks[currentTrackIndex];
    if (!currentTrack || !currentTrack.fileUrl) return;

    const audio = audioRef.current;
    if (!audio) return;

    // Set duration from track data immediately (fallback until audio metadata loads)
    if (currentTrack.duration && currentTrack.duration > 0) {
      setDuration(currentTrack.duration);
    }

    // Set new source if needed
    if (audio.src !== currentTrack.fileUrl) {
      audio.src = currentTrack.fileUrl;
      audio.volume = volumeRef.current ?? volume;
    }

    // Never auto-play here. Queue refreshes and track list updates must not resume playback.
    if (!isPlayingRef.current && audio && !audio.paused) {
      audio.pause();
    }
  }, [
    tracks,
    currentTrackIndex,
    isInitialized,
    userInteracted,
    isPlaying,
    volume,
  ]);

  // Keep refs in sync for stable callbacks
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    userInteractedRef.current = userInteracted;
  }, [userInteracted]);

  // Record play count (single source of truth - only called from PlayerContext)
  const recordPlayCount = useCallback(async (contentId) => {
    if (!contentId) return;

    // Check if already tracked (prevent duplicate calls)
    if (playCountTrackedRef.current.has(contentId)) {
      return;
    }

    try {
      // Mark as tracked immediately to prevent duplicate calls
      playCountTrackedRef.current.add(contentId);

      await httpClient.post(
        `user/play-history/content-play-count/${contentId}`,
        {},
        { skipToast: true, skipLoader: true },
      );
    } catch (error) {
      console.error("Play count error:", error);
      // Remove from tracked set on error so it can be retried
      playCountTrackedRef.current.delete(contentId);
    }
  }, []);

  // Start play count timer
  const startPlayCountTimer = useCallback(
    (contentId) => {
      if (!contentId) return;

      // Already tracked or timer already running for this track
      if (
        playCountTrackedRef.current.has(contentId) ||
        (playCountTimerRef.current &&
          playCountTimerContentIdRef.current === contentId)
      ) {
        return;
      }

      // Clear any existing timer for a different track
      if (playCountTimerRef.current) {
        clearTimeout(playCountTimerRef.current);
        playCountTimerRef.current = null;
      }

      playCountTimerContentIdRef.current = contentId;
      playCountTimerRef.current = setTimeout(() => {
        recordPlayCount(contentId);
        playCountTimerRef.current = null;
        playCountTimerContentIdRef.current = null;
      }, 10000); // Record after 10 seconds of playback
    },
    [recordPlayCount],
  );

  // Play/pause toggle
  const togglePlayPause = useCallback(async () => {
    const currentTrack = tracks[currentTrackIndex];
    if (!currentTrack) return;

    setUserInteracted(true);

    try {
      const audio = audioRef.current;
      if (!audio) return;

      if (audio.paused) {
        await audio.play();
      } else {
        audio.pause();
        // Clear timer if paused
        if (playCountTimerRef.current) {
          clearTimeout(playCountTimerRef.current);
          playCountTimerRef.current = null;
        }
      }
    } catch (err) {
      console.error("Play/Pause error:", err);
      setIsPlaying(false);
    }
  }, [tracks, currentTrackIndex]);

  // Volume change handler
  const handleVolumeChange = useCallback((newVolume) => {
    const normalizedVolume = Math.max(0, Math.min(1, Number(newVolume)));
    setVolume(normalizedVolume);

    const audio = audioRef.current;
    if (audio) {
      audio.volume = normalizedVolume;
    }

    localStorage.setItem("playerVolume", String(normalizedVolume));
  }, []);

  // Seek change handler
  const handleSeekChange = useCallback((value) => {
    const newTime = Number(value);
    setSeek(newTime);
    const audio = audioRef.current;
    if (audio && !isNaN(audio.duration)) {
      try {
        audio.currentTime = newTime;
      } catch (err) {
        console.warn("Seek failed:", err);
      }
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => setError(null), []);

  // Retry queue fetch
  const retryQueueFetch = useCallback(() => {
    setError(null);
    fetchCurrentQueue();
  }, [fetchCurrentQueue]);

  // Context value
  const value = {
    audioRef,
    tracks,
    currentTrackIndex,
    currentTrack: tracks[currentTrackIndex] || null,
    isPlaying,
    seek,
    duration,
    volume,
    isShuffle,
    isRepeat,
    isLoading,
    error,
    togglePlayPause,
    handleNextTrack,
    handlePrevTrack,
    handleSeekChange,
    handleVolumeChange,
    setCurrentTrackIndex: useCallback(
      (index) => {
        const clamped = Math.max(0, Math.min(index, tracks.length - 1));
        setCurrentTrackIndex(clamped);
      },
      [tracks],
    ),
    triggerQueueUpdate,
    playTrackById,
    stopPlayback,
    toggleShuffle: useCallback(() => setIsShuffle((s) => !s), []),
    toggleRepeat: useCallback(() => setIsRepeat((r) => !r), []),
    refreshQueue: fetchCurrentQueue,
    clearError,
    retryQueueFetch,
    playMediaByIndex,
  };

  // Initial queue fetch
  useEffect(() => {
    const token = localStorage.getItem("volumeToken");
    if (!token) {
      setIsInitialized(true);
      return;
    }
    fetchCurrentQueue();
  }, [fetchCurrentQueue]);

  // Refetch when trigger increments
  useEffect(() => {
    if (queueUpdateTrigger > 0 && isInitialized) {
      fetchCurrentQueue();
    }
  }, [queueUpdateTrigger, isInitialized, fetchCurrentQueue]);

  // Reset play count tracking when track changes
  useEffect(() => {
    const currentTrack = tracks[currentTrackIndex];
    if (!currentTrack || !currentTrack._id) return;

    const currentTrackId = currentTrack._id;

    // Only reset if track actually changed
    if (lastTrackIdRef.current !== currentTrackId) {
      // Clear timer when track changes
      if (playCountTimerRef.current) {
        clearTimeout(playCountTimerRef.current);
        playCountTimerRef.current = null;
      }
      // Remove previous track from tracked set to allow re-tracking if played again
      if (lastTrackIdRef.current) {
        playCountTrackedRef.current.delete(lastTrackIdRef.current);
      }
      lastTrackIdRef.current = currentTrackId;
    }
  }, [tracks, currentTrackIndex]);

  // Track play count when track starts playing
  useEffect(() => {
    const currentTrack = tracks[currentTrackIndex];
    if (!currentTrack || !currentTrack._id) return;

    const currentTrackId = currentTrack._id;

    // Start timer when playing and not already tracked
    if (isPlaying && !playCountTrackedRef.current.has(currentTrackId)) {
      // Only start if timer is not already running for this track
      if (!playCountTimerRef.current) {
        startPlayCountTimer(currentTrackId);
      }
    } else if (!isPlaying) {
      // Clear timer if paused (but keep in tracked set so we don't track again on resume)
      if (playCountTimerRef.current) {
        clearTimeout(playCountTimerRef.current);
        playCountTimerRef.current = null;
      }
    }

    return () => {
      // Don't clear timer on cleanup - let it complete if playing
      // Only clear if component unmounts or track changes (handled above)
    };
  }, [isPlaying, tracks, currentTrackIndex, startPlayCountTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (playCountTimerRef.current) {
        clearTimeout(playCountTimerRef.current);
        playCountTimerRef.current = null;
      }
      const audio = audioRef.current;
      if (audio) {
        try {
          audio.pause();
          audio.src = "";
        } catch {}
      }
    };
  }, []);

  return (
    <PlayerContext.Provider value={value}>
      {children}
      <audio
        ref={audioRef}
        style={{ display: "none" }}
        preload="metadata"
        playsInline
        aria-label="Audio player"
      />
    </PlayerContext.Provider>
  );
}
